import { apiClient } from '../../../api/client'
import { useEffect, useState } from 'react'
import { Box, Flex, Spinner, Text, VStack } from '@minetrace/ui'
import { useFormColors, FormSection } from '@minetrace/ui/forms'
import {
  LuClipboardCheck as ClipboardCheck,
  LuMapPin as MapPin,
  LuCalendar as Calendar,
  LuUser as User,
  LuFileText as FileText,
  LuExternalLink as ExternalLink,
  LuImage as ImageIcon,
} from 'react-icons/lu'
import { proveedoresService } from '../../../api/services/proveedores.service'
import type { MinerVerificationResponse, MinerVerificationDetail, MinerVerificationComponentCheck, MinerVerificationEvidence } from '../../../api/models/response/proveedor'

interface Step9VerificationProps {
  companyId?: string
}

// The /programaciones/files/* endpoint sits behind [Authorize], so plain
// <a href> downloads return 401. Fetch with the Bearer token, materialize
// a blob URL, and open that in a new tab.
async function openAuthenticatedFile(url: string) {
  const res = await apiClient.fetchAuthorized(url)
  if (!res.ok) return
  const blob = await res.blob()
  window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer')
}

const STATUS_COLORS: Record<number, { bg: string; color: string; label: string }> = {
  1: { bg: '#f9eccf', color: '#92400E', label: 'Listo' },           // Listo (programado)
  2: { bg: '#e2eaf4', color: '#1E40AF', label: 'En Proceso' },       // EnProceso
  3: { bg: '#D1FAE5', color: '#065F46', label: 'Completado' },       // Completado
  4: { bg: '#D1FAE5', color: '#065F46', label: 'Finalizado' },       // Finalizado
  5: { bg: '#fae1db', color: '#991B1B', label: 'Pausado' },          // Pausado
}

export default function Step9Verification({ companyId }: Step9VerificationProps) {
  const c = useFormColors()
  const [verifications, setVerifications] = useState<MinerVerificationResponse[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lightboxImage, setLightboxImage] = useState<{ url: string; name: string } | null>(null)

  useEffect(() => {
    if (!companyId) return
    let cancelled = false
    proveedoresService.getMinerVerifications(companyId)
      .then(data => {
        if (cancelled) return
        setVerifications(data)
        setError(null)
        setLoading(false)
      })
      .catch(err => {
        if (cancelled) return
        setError(err?.message ?? 'Error cargando verificaciones')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [companyId])

  return (
    <VStack gap={6} alignItems="stretch">
      <FormSection icon={ClipboardCheck} titleTid="proveedores.wizard.step9.title" descriptionTid="proveedores.wizard.step9.description">
        {loading && (
          <Flex p={8} justifyContent="center" alignItems="center" gap={2}>
            <Spinner size="sm" />
            <Text fontSize="sm" color={c.textMuted}>Cargando verificaciones...</Text>
          </Flex>
        )}

        {error && !loading && (
          <Box p={4} bg="#fcefeb" borderRadius="sm" border="1px solid #FECACA">
            <Text fontSize="sm" color="#991B1B">{error}</Text>
          </Box>
        )}

        {!loading && !error && verifications && verifications.length === 0 && (
          <EmptyState colorMuted={c.textMuted} colorDimmed={c.textDimmed} />
        )}

        {!loading && !error && verifications && verifications.length > 0 && (
          <VStack gap={4} alignItems="stretch">
            {verifications.map(v => (
              <MiningRightVerificationCard
                key={v.miningRightId}
                verification={v}
                onOpenImage={setLightboxImage}
              />
            ))}
          </VStack>
        )}
      </FormSection>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
    </VStack>
  )
}

function EmptyState({ colorMuted, colorDimmed }: { colorMuted: string; colorDimmed: string }) {
  return (
    <Box p={8} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="sm">
      <ClipboardCheck size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
      <Text fontSize="sm" color={colorMuted}>No hay derechos mineros registrados</Text>
      <Text fontSize="xs" color={colorDimmed} mt={1}>Las verificaciones se mostrarán cuando existan derechos mineros para fiscalizar.</Text>
    </Box>
  )
}

function MiningRightVerificationCard({
  verification,
  onOpenImage,
}: {
  verification: MinerVerificationResponse
  onOpenImage: (img: { url: string; name: string }) => void
}) {
  const c = useFormColors()
  const d = verification.detail
  const statusInfo = d ? STATUS_COLORS[d.detailStatus] ?? { bg: '#efe8dd', color: '#4f4842', label: d.detailStatusName } : null

  return (
    <Box border="1px solid" borderColor={c.border} borderRadius="md" overflow="hidden">
      <Flex p={4} bg={c.bgHover} alignItems="center" justifyContent="space-between" gap={3} flexWrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="700" color={c.text}>{verification.miningRightName}</Text>
          <Text fontSize="xs" color={c.textMuted} fontFamily="monospace" mt={0.5}>{verification.miningRightUniqueCode}</Text>
        </Box>
        {statusInfo ? (
          <Box px={3} py={1} borderRadius="full" fontSize="11px" fontWeight="700" textTransform="uppercase" bg={statusInfo.bg} color={statusInfo.color}>
            {statusInfo.label}
          </Box>
        ) : (
          <Box px={3} py={1} borderRadius="full" fontSize="11px" fontWeight="700" textTransform="uppercase" bg="#efe8dd" color="#857b71">
            Sin programar
          </Box>
        )}
      </Flex>

      {d == null ? (
        <Box p={4}>
          <Text fontSize="xs" color={c.textDimmed}>No hay verificación de campo programada para este derecho minero.</Text>
        </Box>
      ) : (
        <VerificationDetailBody detail={d} onOpenImage={onOpenImage} />
      )}
    </Box>
  )
}

function VerificationDetailBody({
  detail,
  onOpenImage,
}: {
  detail: MinerVerificationDetail
  onOpenImage: (img: { url: string; name: string }) => void
}) {
  const c = useFormColors()
  const acta = detail.acta

  return (
    <Box p={4}>
      <Flex gap={4} flexWrap="wrap" mb={4}>
        {detail.scheduleCode && (
          <InfoBlock icon={<FileText size={12} />} label="Programación" value={`${detail.scheduleCode}${detail.scheduleName ? ` · ${detail.scheduleName}` : ''}`} colors={c} />
        )}
        {detail.geologistNames.length > 0 && (
          <InfoBlock icon={<User size={12} />} label="Geólogo(s)" value={detail.geologistNames.join(', ')} colors={c} />
        )}
        {detail.startedAt && (
          <InfoBlock icon={<Calendar size={12} />} label="Inicio" value={formatDateTime(detail.startedAt)} colors={c} />
        )}
        {detail.completedAt && (
          <InfoBlock icon={<Calendar size={12} />} label="Finalización" value={formatDateTime(detail.completedAt)} colors={c} />
        )}
        {(detail.startLatitude != null && detail.startLongitude != null) && (
          <InfoBlock icon={<MapPin size={12} />} label="GPS Inicio" value={`${detail.startLatitude.toFixed(6)}, ${detail.startLongitude.toFixed(6)}`} colors={c} />
        )}
        {(detail.completedLatitude != null && detail.completedLongitude != null) && (
          <InfoBlock icon={<MapPin size={12} />} label="GPS Fin" value={`${detail.completedLatitude.toFixed(6)}, ${detail.completedLongitude.toFixed(6)}`} colors={c} />
        )}
      </Flex>

      {detail.componentChecks.length > 0 && (
        <Box mb={4}>
          <Text fontSize="xs" fontWeight="700" color={c.textMuted} textTransform="uppercase" mb={2}>Componentes verificados ({detail.componentChecks.length})</Text>
          <VStack gap={3} alignItems="stretch">
            {detail.componentChecks.map(cc => (
              <ComponentCheckRow key={cc.id} check={cc} onOpenImage={onOpenImage} />
            ))}
          </VStack>
        </Box>
      )}

      {acta && (
        <Box mt={4} p={3} bg={c.bgHover} borderRadius="md">
          <Flex justifyContent="space-between" alignItems="flex-start" gap={3} flexWrap="wrap">
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="700" color={c.textMuted} textTransform="uppercase" mb={1}>Acta de fiscalización</Text>
              {acta.activityStatus && (
                <Text fontSize="sm" color={c.text}>Estado de actividad: <Text as="span" fontWeight="600">{acta.activityStatus}</Text></Text>
              )}
              {acta.fiscalizadorComments && (
                <Box mt={2}>
                  <Text fontSize="xs" color={c.textDimmed}>Comentarios del fiscalizador</Text>
                  <Text fontSize="sm" color={c.text}>{acta.fiscalizadorComments}</Text>
                </Box>
              )}
              {acta.mineroComments && (
                <Box mt={2}>
                  <Text fontSize="xs" color={c.textDimmed}>Comentarios del minero</Text>
                  <Text fontSize="sm" color={c.text}>{acta.mineroComments}</Text>
                </Box>
              )}
            </Box>
            {acta.pdfStoragePath && (
              <Box
                as="button"
                {...{ type: 'button' }}
                onClick={() => openAuthenticatedFile(
                  `${import.meta.env.VITE_API_BASE_URL}/programaciones/${detail.scheduleId}/details/${detail.id}/acta-pdf`
                )}
                display="inline-flex"
                alignItems="center"
                gap={1}
                px={3}
                py={2}
                bg={c.accent}
                color="white"
                borderRadius="md"
                fontSize="xs"
                fontWeight="600"
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
              >
                <ExternalLink size={12} />
                Descargar Acta PDF
              </Box>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  )
}

function ComponentCheckRow({
  check,
  onOpenImage,
}: {
  check: MinerVerificationComponentCheck
  onOpenImage: (img: { url: string; name: string }) => void
}) {
  const c = useFormColors()
  const distanceText = check.distanceMeters != null ? `${check.distanceMeters.toFixed(1)} m` : '—'
  const distanceColor = check.isWithinMargin ? '#1d6664' : '#9a3020'

  return (
    <Box p={3} border="1px solid" borderColor={c.border} borderRadius="md">
      <Flex justifyContent="space-between" alignItems="flex-start" gap={3} mb={2} flexWrap="wrap">
        <Box flex={1} minW="200px">
          <Text fontSize="sm" fontWeight="600" color={c.text}>{check.componentName ?? 'Componente sin nombre'}</Text>
          <Text fontSize="xs" color={c.textMuted}>Estado: {check.checkStatusName}</Text>
        </Box>
        <Box textAlign="right">
          <Text fontSize="xs" color={c.textDimmed}>Distancia</Text>
          <Text fontSize="sm" fontWeight="700" color={distanceColor}>{distanceText}</Text>
        </Box>
      </Flex>

      <Flex gap={4} flexWrap="wrap" mb={2}>
        {(check.expectedLatitude != null && check.expectedLongitude != null) && (
          <Box>
            <Text fontSize="10px" color={c.textDimmed} textTransform="uppercase">Esperado</Text>
            <Text fontSize="xs" color={c.text} fontFamily="monospace">{check.expectedLatitude.toFixed(6)}, {check.expectedLongitude.toFixed(6)}</Text>
          </Box>
        )}
        {(check.capturedLatitude != null && check.capturedLongitude != null) && (
          <Box>
            <Text fontSize="10px" color={c.textDimmed} textTransform="uppercase">Capturado</Text>
            <Text fontSize="xs" color={c.text} fontFamily="monospace">
              {check.capturedLatitude.toFixed(6)}, {check.capturedLongitude.toFixed(6)}
              {check.capturedAltitude != null ? ` · alt ${check.capturedAltitude.toFixed(1)} m` : ''}
            </Text>
          </Box>
        )}
      </Flex>

      {check.evidences.length > 0 && (
        <Box mt={2}>
          <Text fontSize="10px" color={c.textDimmed} textTransform="uppercase" mb={1}>Evidencias ({check.evidences.length})</Text>
          <Flex gap={2} flexWrap="wrap">
            {check.evidences.map(ev => (
              <EvidenceThumbnail key={ev.id} evidence={ev} onOpen={onOpenImage} />
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  )
}

function EvidenceThumbnail({
  evidence,
  onOpen,
}: {
  evidence: MinerVerificationEvidence
  onOpen: (img: { url: string; name: string }) => void
}) {
  const isImage = (evidence.contentType ?? '').startsWith('image/')
  const url = evidence.storagePath ? proveedoresService.getVerificationFileUrl(evidence.storagePath) : null

  if (!url) {
    return (
      <Box w="80px" h="80px" bg="#efe8dd" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
        <ImageIcon size={20} color="#b5aa9d" />
      </Box>
    )
  }

  if (!isImage) {
    return (
      <Box
        as="button"
        {...{ type: 'button' }}
        onClick={() => openAuthenticatedFile(url)}
        w="80px"
        h="80px"
        bg="#eef3f9"
        borderRadius="md"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={1}
        cursor="pointer"
        title={evidence.fileName ?? evidence.typeName}
      >
        <FileText size={20} color="#1E40AF" />
        <Text fontSize="9px" color="#1E40AF" mt={0.5} textAlign="center" lineClamp={1} maxW="full">
          {evidence.fileName?.split('.').pop()?.toUpperCase() ?? evidence.typeName}
        </Text>
      </Box>
    )
  }

  return (
    <Box
      as="button" type="button"
      onClick={() => onOpen({ url, name: evidence.fileName ?? evidence.typeName })}
      w="80px"
      h="80px"
      borderRadius="md"
      overflow="hidden"
      border="1px solid #e8dfd1"
      cursor="pointer"
      _hover={{ borderColor: '#3d6fa8' }}
      title={evidence.fileName ?? evidence.typeName}
      bg="#faf6f0"
    >
      <AuthImage url={url} alt={evidence.fileName ?? 'evidencia'} />
    </Box>
  )
}

function AuthImage({ url, alt }: { url: string; alt: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    let createdUrl: string | null = null
    apiClient.fetchAuthorized(url)
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => {
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setBlobUrl(createdUrl)
      })
      .catch(() => { /* swallow */ })
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [url])

  if (!blobUrl) return <Box w="full" h="full" bg="#efe8dd" />
  return <img src={blobUrl} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
}

function Lightbox({ image, onClose }: { image: { url: string; name: string }; onClose: () => void }) {
  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0,0,0,0.85)"
      zIndex={9999}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClose}
      cursor="pointer"
    >
      <Box maxW="90vw" maxH="90vh" onClick={e => e.stopPropagation()}>
        <AuthImageFull url={image.url} alt={image.name} />
        <Text mt={2} color="white" fontSize="sm" textAlign="center">{image.name}</Text>
      </Box>
    </Box>
  )
}

function AuthImageFull({ url, alt }: { url: string; alt: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    let createdUrl: string | null = null
    apiClient.fetchAuthorized(url)
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => {
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setBlobUrl(createdUrl)
      })
      .catch(() => { /* swallow */ })
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [url])

  if (!blobUrl) return <Spinner color="white" />
  return <img src={blobUrl} alt={alt} style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }} />
}

function InfoBlock({ icon, label, value, colors }: { icon: React.ReactNode; label: string; value: string; colors: ReturnType<typeof useFormColors> }) {
  return (
    <Box>
      <Flex alignItems="center" gap={1} mb={0.5}>
        <Box color={colors.textDimmed}>{icon}</Box>
        <Text fontSize="10px" color={colors.textDimmed} textTransform="uppercase">{label}</Text>
      </Flex>
      <Text fontSize="sm" color={colors.text}>{value}</Text>
    </Box>
  )
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}
